import './DeviceOptionsPanel.sass'
import CollapsablePanel from '../../../common/CollapsablePanel/CollapsablePanel';
import useDeviceInfo from 'src/services/device/common/useDeviceInfo';
import Switch from 'src/hci/common/Switch/Switch';
import { useEffect, useState, useRef, useMemo } from 'react';
import useDeviceTelemetries from 'src/services/device/common/useDeviceTelemetries';
import { EntityType } from '../../../EntityList/EntityList';
import { getDeviceTelemetries, getSubDeviceTelemetries } from 'src/services/device/common/deviceUtils';
import { DISABLE_SIMULATION_STORAGE_KEY, DjiDockMode, KEEP_DOCK_ALIVE_DURATION, KEEP_DOCK_ALIVE_STORAGE_KEY } from 'src/services/device/common/deviceConstants';
import { sendActivateRemoteDebugging, sendChargeOpen, sendDeactivateRemoteDebugging } from 'src/services/device/common/remoteDebugUtils';
import { useBroadcastToFrontend } from 'src/helper/useBroadcastToFrontend';
import Separator from 'src/hci/common/Separator/Separator';
import TextInput from 'src/hci/common/TextInput/TextInput';
import InputDescription from 'src/hci/common/InputDescription/InputDescription';
import { clientBroadcastMethod } from 'src/services/common/constants';
import useCurrentUserId from 'src/helper/useCurrentUserId';
import Button from 'src/hci/common/Button/Button';
import useDialog from 'src/helper/useDialog';
import { TbBattery4 } from 'react-icons/tb';

export default function DeviceOptionsPanel({ entity, onClose, collapsed, missionController }) {
  const deviceInfo = useDeviceInfo(entity?.id);
  const userId = useCurrentUserId();
  const dialog = useDialog();
  const subDeviceOnlineStatus = useDeviceTelemetries(entity?.id, telemetry => telemetry?.sub_device?.device_online_status);
  const [uavSwitchActive, setUavSwitchActive] = useState(false);
  const [uavDataEnabled, setUavDataEnabled] = useState(false);
  const uavDataSendIntervalId = useRef();
  const keepAliveConfigIntervalId = useRef();
  const [keepAliveConfig, setKeepAliveConfig] = useState();
  const broadcastToFrontend = useBroadcastToFrontend();
  const [keepAliveDuration, setKeepAliveDuration] = useState(120);
  const [disableSimulation, setDisableSimulation] = useState(false);

  const remainingKeepAliveDuration = (() => {
    const { duration, lastMissionTimestamp } = keepAliveConfig || {};
    if(!duration || !lastMissionTimestamp) return 0;

    const elapsedTime = Date.now() - lastMissionTimestamp;
    const remainingTime = duration - elapsedTime;

    const remained = Math.floor(remainingTime / 1000);
    return remained > 0 ? remained : 0;
  })();

  const getAllKeepAliveConfig = () => {
    const keepAliveConfig = localStorage.getItem(KEEP_DOCK_ALIVE_STORAGE_KEY);
    const keepAliveConfigParsed = JSON.parse(keepAliveConfig) || {};
    return keepAliveConfigParsed;
  }

  const saveKeepAliveConfig = (config) => {
    let keepAliveConfig = getAllKeepAliveConfig();

    if(!config) {
      delete keepAliveConfig[entity?.id];
      broadcastToFrontend(clientBroadcastMethod.keepAlive.DOCK_DRONE_KEEP_ALIVE_DISABLED, { 
        deviceId: entity?.id,
        sourceUserId: userId,
      });
    } else {
      keepAliveConfig[entity?.id] = {
        ...keepAliveConfig[entity?.id],
        ...config
      };

      broadcastToFrontend(clientBroadcastMethod.keepAlive.DOCK_DRONE_KEEP_ALIVE_ENABLED, { 
        deviceId: entity?.id,
        sourceUserId: userId,
        config: {
          ...keepAliveConfig[entity?.id],
          ...config
        }
      });
    }

    localStorage.setItem(KEEP_DOCK_ALIVE_STORAGE_KEY, JSON.stringify(keepAliveConfig));
  }

  const handleKeepAliveConfigChange = (config) => {
    setKeepAliveConfig(config);
    saveKeepAliveConfig(config);

    if(!config) {
      console.log('⌛️❌ Keep alive cancelled for dock', entity?.id);
      sendDeactivateRemoteDebugging(entity?.id);
    }
  }

  const handleKeepAliveDurationChange = (duration) => {
    const parsedDuration = parseInt(duration, 10);

    if(isNaN(parsedDuration)) return;
    setKeepAliveDuration(parsedDuration);
  }

  const handleClose = () => {
    onClose?.();
  }

  const getSimulationDisableStatus = () => {
    const simulationStatus = localStorage.getItem(DISABLE_SIMULATION_STORAGE_KEY);
    const simulationStatusParsed = JSON.parse(simulationStatus) || {};

    setDisableSimulation(!!simulationStatusParsed[entity?.id]);
    return !!simulationStatusParsed[entity?.id];
  }

  const saveSimulationDisableStatus = (status) => {
    let simulationStatus = localStorage.getItem(DISABLE_SIMULATION_STORAGE_KEY);
    const simulationStatusParsed = JSON.parse(simulationStatus) || {};

    simulationStatusParsed[entity?.id] = !!status;
    localStorage.setItem(DISABLE_SIMULATION_STORAGE_KEY, JSON.stringify(simulationStatusParsed));
    setDisableSimulation(!!status);
  }

  const handleSimulationDisableStatusChange = (status) => {
    saveSimulationDisableStatus(status);
  }

  const handleChargeOpenClick = () => {
    sendActivateRemoteDebugging(entity?.id).then(() => {
      sendChargeOpen(entity?.id).then(() => {
        dialog.fire({
          title: <b>Full Battery Charge</b>,
          text: "Battery chraging process has been started.",
          icon: 'success',
        });
      }).catch(() => {
        dialog.fire({
          title: <b>Full Battery Charge</b>,
          text: "Battery charging process failed.",
          icon: 'error',
        });
      });
    }).catch(() => {
      dialog.fire({
        title: <b>Full Battery Charge</b>,
        text: "Battery charging process failed.",
        icon: 'error',
      });
    });
  }

  const sendUavData = (data) => {
    const endpoint = 'https://sandbox-public-api.safesky.app/v1/uav';
    const xApiKey = '18371ec0cb8146b4bcab4b1738eef46e';

    const normalizedData = [{
      ...data,
      altitude: Math.floor(data.altitude),
      course: Math.floor(data.course),
      ground_speed: Math.floor(data.ground_speed)
    }];

    fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': xApiKey
      },
      body: JSON.stringify(normalizedData)
    });
  
    console.log('ℹ️ UAV data', normalizedData);
  }

  useEffect(() => {
    if(subDeviceOnlineStatus || entity?.entityType === EntityType.DRONE) {
      setUavDataEnabled(!!uavSwitchActive);
    }
    else setUavDataEnabled(false);
  }, [entity, subDeviceOnlineStatus, uavSwitchActive]);

  useEffect(() => {
    if(uavDataEnabled && !uavDataSendIntervalId.current) {
      uavDataSendIntervalId.current = setInterval(() => {
        let data = {
          id: 'uav2234',
          call_sign: 'DJI-' + (deviceInfo?.serialNumber || entity.id)
        };

        if(subDeviceOnlineStatus) {
          const telemetries = getSubDeviceTelemetries(entity.id);

          data = {
            ...data,
            latitude: telemetries?.latitude,
            longitude: telemetries?.longitude,
            altitude: telemetries?.elevation || 0,
            course: telemetries?.attitude_head || 0,
            ground_speed: telemetries?.horizontal_speed || 0,
          };
        } else {
          const telemetries = getDeviceTelemetries(entity.id);
          const flightStatus = telemetries?.flightControl?.flightStatus;
          const speed = Math.sqrt(Math.pow(telemetries?.velocity?.x, 2) + Math.pow(telemetries?.velocity?.y, 2));
          let droneAltitude;

          if(flightStatus === 2 && telemetries?.gps)
            droneAltitude = telemetries?.altitudeInfo?.asl - telemetries?.gps?.homepoint?.alt;

          data = {
            ...data,
            latitude: telemetries?.gps?.lat,
            longitude: telemetries?.gps?.long,
            altitude: droneAltitude || 0,
            course: telemetries?.facing?.heading || 0,
            ground_speed: speed || 0,
          };
        }

        sendUavData(data);
      }, 1000);
    } else if(!uavDataEnabled) {
      clearInterval(uavDataSendIntervalId.current);
      uavDataSendIntervalId.current = null;
      
      console.log('ℹ️ UAV data sending stopped');
    }
  }, [uavDataEnabled]);

  useEffect(() => {
    keepAliveConfigIntervalId.current = setInterval(() => {
      const keepAliveConfig = getAllKeepAliveConfig();
      setKeepAliveConfig(keepAliveConfig[entity?.id]);
    }, 5000);

    getSimulationDisableStatus();
  }, [entity?.id, setKeepAliveConfig]);

  useEffect(() => {
    return () => {
      clearInterval(keepAliveConfigIntervalId.current);
      clearInterval(uavDataSendIntervalId.current);
    }
  }, []);

  return (
    <div className="device-options-panel">
      <CollapsablePanel title="Device Options" collapsed={collapsed}>
        <div className="options-items">
          {entity?.entityType === EntityType.DOCK && (
            <>
              <div className="item">
                <div style={{ marginBottom: '10px' }}>
                  <Switch checked={!!keepAliveConfig?.duration} onCheckedChange={checked => handleKeepAliveConfigChange(checked ? { 
                    ...keepAliveConfig, 
                    duration: keepAliveDuration > 0 ? keepAliveDuration * 60 * 1000 : KEEP_DOCK_ALIVE_DURATION,
                    lastMissionTimestamp: Date.now(),
                    takeoffDelay: 1000,
                  } : false)} />
                  Keep Alive
                </div>
                <TextInput 
                  onChange={e => handleKeepAliveDurationChange(e.target.value)} 
                  value={keepAliveConfig?.duration ? `~ ${Math.floor(remainingKeepAliveDuration / 60)}:${(remainingKeepAliveDuration % 60)}` : keepAliveDuration} 
                  postfix={remainingKeepAliveDuration ? 'remaining time' : 'minutes'} 
                  disabled={!!keepAliveConfig?.duration} 
                />
                <InputDescription>{keepAliveConfig?.duration ? 'Remaining keep-alive time' : 'Target keep-alive duration in minutes (resets after each mission)'}</InputDescription>
              </div>
              <Separator />
              <div className="item">
                <div>
                  <Switch checked={disableSimulation} onCheckedChange={handleSimulationDisableStatusChange} />
                  Disable Simulation
                </div>
              </div>
              <Separator />
              <div className="item">
                <Button onClick={handleChargeOpenClick} size="2">
                  <TbBattery4 className='button-icon' /> Charge to 100%
                </Button>
                <InputDescription>This option only works in debugging mode</InputDescription>
              </div>
              <Separator />
            </>
          )}
          <div className="item">
            <Switch onCheckedChange={checked => setUavSwitchActive(checked)} />
            Send UAV Data
          </div>
        </div>
      </CollapsablePanel>
    </div>
  )
}
